// Lists all unique brands from the products catalog as searchable chips.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import StaticPage from "../../components/StaticPage";
import { api } from "../../api/client";

const AllBrands = () => {
    const [brands, setBrands] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Pull a wide product slice and dedupe brand values client-side.
        api.get("/products", { params: { page_size: 200, sort: "name" } })
            .then(({ data }) => {
                const set = new Set();
                (data.items || []).forEach((p) => p.brand && set.add(p.brand.trim()));
                setBrands(Array.from(set).sort((a, b) => a.localeCompare(b)));
            })
            .finally(() => setLoading(false));
    }, []);

    const filtered = q.trim() ? brands.filter((b) => b.toLowerCase().includes(q.trim().toLowerCase())) : brands;
    // Group by first letter for the A-Z index
    const groups = filtered.reduce((acc, b) => {
        const k = b[0].toUpperCase();
        (acc[k] = acc[k] || []).push(b);
        return acc;
    }, {});

    return (
        <StaticPage title="All Brands" crumb="Brands" testId="page-brands" intro={`${brands.length} brands available on PILLS. Filter by name or jump to a letter.`}>
            <div className="mb-6 relative max-w-md">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search brand…" className="w-full rounded-lg border border-zinc-200 bg-white py-2.5 pl-10 pr-3 text-sm outline-none ring-brand-200 focus:ring-2 dark:border-white/10 dark:bg-white/5" data-testid="brands-search" />
            </div>
            {loading ? (
                <div className="h-32 skeleton rounded-2xl" />
            ) : Object.keys(groups).length === 0 ? (
                <p className="rounded-2xl border border-dashed border-zinc-200 bg-zinc-50 p-6 text-sm text-zinc-500">No brand matches "{q}".</p>
            ) : (
                <div className="space-y-6">
                    {Object.keys(groups).sort().map((letter) => (
                        <section key={letter}>
                            <h2 className="mb-2 text-sm font-bold text-brand-700">{letter}</h2>
                            <div className="flex flex-wrap gap-2">
                                {groups[letter].map((b) => (
                                    <Link key={b} to={`/products?q=${encodeURIComponent(b)}`} className="rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-xs text-zinc-700 transition-colors hover:border-brand-300 hover:text-brand-700 dark:border-white/10 dark:bg-white/5 dark:text-zinc-200" data-testid={`brand-chip-${b}`}>
                                        {b}
                                    </Link>
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            )}
        </StaticPage>
    );
};

export default AllBrands;
